/** A link's QR code, opened by its QR button (click only — never on hover): a 160 px code on
 *  screen with the link under it, and **Download PNG** saves the same code at print size.
 *  The tour's self-registration links' pattern (`tours/record/panels/selfreg/QrPopover`),
 *  for any link: the itinerary record's deep link. The code is drawn here from the link
 *  (`qrcode.react`), so nothing is fetched to show it. */
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, Popover, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useId, useRef } from 'react';

/** The PNG a download saves: large enough to print (the popover shows a 160 px one). */
export const QR_CODE_DOWNLOAD_PX = 1024;

export interface QrCodePopoverProps {
  /** What the code encodes; null: closed. */
  value: string | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The dialog's title ("QR code of the deep link"). */
  title: string;
  /** The PNG's file name ("itinerary-303-deep-link.png"). */
  fileName: string;
}

export function QrCodePopover({ value, anchorEl, onClose, title, fileName }: QrCodePopoverProps) {
  const printCanvas = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const download = () => {
    const canvas = printCanvas.current?.querySelector('canvas');
    if (!canvas || !value) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = fileName;
    a.click();
  };
  return (
    <Popover
      open={Boolean(value && anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: { p: 1.5, display: 'grid', gap: 1, justifyItems: 'center', maxWidth: 260 },
        } as object,
      }}
    >
      {value && (
        <>
          <Typography id={titleId} variant="subtitle2">
            {title}
          </Typography>
          <Box data-qr-code>
            <QRCodeCanvas value={value} size={160} marginSize={2} title={value} />
          </Box>
          {/* What Download PNG saves: the same code at print size, not shown. */}
          <Box ref={printCanvas} data-qr-code-print aria-hidden sx={{ display: 'none' }}>
            <QRCodeCanvas value={value} size={QR_CODE_DOWNLOAD_PX} marginSize={4} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', textAlign: 'center' }}>
            {value}
          </Typography>
          <Button size="small" startIcon={<DownloadIcon />} onClick={download}>
            Download PNG ({QR_CODE_DOWNLOAD_PX} px)
          </Button>
        </>
      )}
    </Popover>
  );
}
